import {useContext, useEffect, useState} from 'react';
import AppContext from "../../../context";

import Meta from "../../components/Meta";
import Header from "../../components/Header";
import Data from "../../../repository";
import Loading from "../../components/Loading";
import SumupWidget from "../../components/SumupWidget";

import styles from '../../../styles/ChoosePlan.module.scss';
import moment from "moment/moment";
import { trackMetaEvent, storePendingPurchase } from '../../../utils/metaPixel';

function ChoosePlan() {
    const context = useContext(AppContext);

    const [loading, setLoading] = useState(true);
    const [membershipPlans, setMembershipPlans] = useState([]);
    const [memberships, setMemberships] = useState([]);
    const [error, setError] = useState(null);
    const [selectedPaymentTypes, setSelectedPaymentTypes] = useState({}); // Track payment type per plan
    const [discountInputs, setDiscountInputs] = useState({});
    const [selectedDiscountCodes, setSelectedDiscountCodes] = useState({});
    const [discountLoadingPlanId, setDiscountLoadingPlanId] = useState(null);
    const [sumupCheckoutId, setSumupCheckoutId] = useState(null);
    const [sumupSetupId, setSumupSetupId] = useState(null);
    const [sumupPolling, setSumupPolling] = useState(false);

    const paymentProcessor = String(context.domain?.paymentProcessor || '').toLowerCase();
    const isWorldpay = paymentProcessor === 'worldpay';
    const isSumup = paymentProcessor === 'sumup';
    // Sessions-type businesses have no real "membership" concept - this page is reused here
    // purely to sell the Paid Extras plan, so the copy is adjusted accordingly.
    const isPaidExtrasContext = context.domain?.businessType === 'sessions';

    useEffect(async () => {
        setMembershipPlans(await fetchMembershipPlans());
        setMemberships(await fetchMemberships());
        setLoading(false);
    }, [])

    const fetchMembershipPlans = async () => {
        const result = await Data.fetchMembershipPlans();
        return result.data.sort((a, b) => a.amount - b.amount);
    }

    const fetchMemberships = async () => {
        if (isWorldpay) {
            const result = await Data.fetchWorldpaySubscriptions();
            return result.data.filter(sub => sub.type === 'membership');
        }
        if (isSumup) {
            const result = await Data.fetchSumupSubscriptions();
            return result.data.filter(sub => sub.type === 'membership');
        }
        const result = await Data.fetchMemberships();
        return result.data;
    }

    const subscribe = async (membershipPlanId, paymentType) => {
        try {
            setError(null);
            setLoading(true);
            const discountCode = selectedDiscountCodes[membershipPlanId] || null;
            const plan = membershipPlans.find(p => p._id === membershipPlanId);
            const pixelId = context.domain?.metaPixelEnabled ? context.domain?.metaPixelId : null;
            const currency = context.domain?.metaCurrency || 'GBP';
            const discountedAmount = plan ? getDiscountedAmount(plan) : 0;
            if (pixelId && plan) {
                trackMetaEvent({
                    pixelId,
                    eventName: 'InitiateCheckout',
                    params: {
                        content_type: 'membership',
                        content_ids: [membershipPlanId],
                        content_name: plan.name,
                        value: discountedAmount / 100,
                        currency
                    }
                });
                storePendingPurchase('membership', { value: discountedAmount / 100, name: plan.name, currency, planId: membershipPlanId });
            }
            if (isWorldpay) {
                const result = await Data.createWorldpayMembershipSubscription(membershipPlanId, paymentType, discountCode);
                window.location.href = result.data.url;
            } else if (isSumup) {
                const result = await Data.createSumupMembershipSubscription(membershipPlanId, paymentType, discountCode);
                setSumupCheckoutId(result.data.id);
                setSumupSetupId(result.data.setupId);
                setSumupPolling(true);
                setLoading(false);
            } else {
                const result = await Data.subscribeToMembershipPlan(membershipPlanId, paymentType, discountCode);
                window.location.href = result.data.url;
            }
        } catch (error) {
            console.error(error);
            const errorMessage = error.response?.data?.message || 'Failed to create subscription. Please try again.';
            setError(errorMessage);
            setLoading(false);
        }
    }

    const applyDiscountCode = async (planId) => {
        const code = discountInputs[planId]?.trim();
        if (!code) return;

        try {
            setError(null);
            setDiscountLoadingPlanId(planId);
            const result = await Data.checkDiscountCode(code);
            setSelectedDiscountCodes(prev => ({
                ...prev,
                [planId]: result.data
            }));
        } catch (error) {
            setSelectedDiscountCodes(prev => {
                const next = { ...prev };
                delete next[planId];
                return next;
            });
            setError(error.response?.data?.message || 'Discount code provided is not valid.');
        } finally {
            setDiscountLoadingPlanId(null);
        }
    }

    const removeDiscountCode = (planId) => {
        setDiscountInputs(prev => ({
            ...prev,
            [planId]: ''
        }));
        setSelectedDiscountCodes(prev => {
            const next = { ...prev };
            delete next[planId];
            return next;
        });
    }

    const getDiscountedAmount = (membershipPlan) => {
        const discountCode = selectedDiscountCodes[membershipPlan._id];
        if (!discountCode) return membershipPlan.amount;
        if (discountCode.discountType === 'amount') return Math.max(0, membershipPlan.amount - (discountCode.amountOff || 0));
        return Math.max(0, Math.round(membershipPlan.amount - (membershipPlan.amount * ((discountCode.percentageOff || 0) / 100))));
    }

    const getPaymentType = (planId) => {
        return selectedPaymentTypes[planId] || 'subscription';
    }

    const setPaymentType = (planId, type) => {
        setSelectedPaymentTypes(prev => ({
            ...prev,
            [planId]: type
        }));
    }

    useEffect(() => {
        if (!isSumup || !sumupCheckoutId || !sumupPolling || !sumupSetupId) return;

        let cancelled = false;
        const intervalId = setInterval(async () => {
            if (cancelled) return;
            try {
                const result = await Data.verifySumupSubscription(sumupSetupId);
                if (result?.status === 200 || result?.data?.subscription) {
                    window.location.href = `/dashboard/membership?paymentStatus=success`;
                }
            } catch (err) {
                // keep polling until the payment settles
            }
        }, 3000);

        const timeoutId = setTimeout(() => {
            if (!cancelled) {
                setSumupPolling(false);
                clearInterval(intervalId);
            }
        }, 180000);

        return () => {
            cancelled = true;
            clearInterval(intervalId);
            clearTimeout(timeoutId);
        };
    }, [isSumup, sumupCheckoutId, sumupPolling, sumupSetupId]);

    const shouldShowPaymentTypeSelector = (plan) => {
        // Show selector if plan supports both payment types or if not specified
        return !plan.paymentType || plan.paymentType === 'both';
    }

    const getAvailablePaymentTypes = (plan) => {
        if (!plan.paymentType || plan.paymentType === 'both') {
            return ['subscription', 'one-time'];
        } else if (plan.paymentType === 'subscription') {
            return ['subscription'];
        } else if (plan.paymentType === 'one-time') {
            return ['one-time'];
        }
        return ['subscription'];
    }

    if(loading) return <div className={styles.choosePlan}>
        <Loading button={false} />
    </div>

    const currentMembership = memberships[memberships.length - 1]

    const renderMembershipStatus = () => {
        if (!currentMembership) return null;
        const label = isPaidExtrasContext ? 'Paid Extras' : 'membership';

        if (isWorldpay) {
            // Worldpay subscription status
            if (currentMembership.status === "cancelled") {
                return (
                    <div className={styles.choosePlan}>
                        <p>Your {label} was <strong>cancelled</strong> on <strong>{moment(currentMembership.cancelledAt).format('Do MMMM')}</strong>. Select a plan below to set up a new one.</p>
                    </div>
                );
            }
            return (
                <div className={styles.choosePlan}>
                    <p>Your {label} is currently <strong>inactive</strong>.</p>
                </div>
            );
        }

        // Stripe subscription status
        if (currentMembership.status === "canceled") {
            if (currentMembership.cancellation_details?.reason === "payment_failed") {
                return (
                    <div className={styles.choosePlan}>
                        <p>Your {label} was <strong>cancelled due to an overdue payment</strong>. Select a plan below to set up a new one.</p>
                    </div>
                );
            }
            return (
                <div className={styles.choosePlan}>
                    <p>Your {label} was <strong>cancelled</strong> on <strong>{moment(currentMembership.canceled_at * 1000).format('Do MMMM')}</strong>. Select a plan below to set up a new one.</p>
                </div>
            );
        }

        return (
            <div className={styles.choosePlan}>
                <p>Your {label} is currently <strong>inactive</strong>.</p>
            </div>
        );
    };

    if (isSumup && sumupCheckoutId) {
        return (
            <div className={styles.choosePlan}>
                <SumupWidget
                    checkoutId={sumupCheckoutId}
                    onSuccess={async () => {
                        try {
                            await Data.verifySumupSubscription(sumupSetupId);
                        } catch (err) {
                            // Subscription may have already been created via polling — proceed
                        }
                        window.location.href = `/dashboard/membership?paymentStatus=success`;
                    }}
                    onError={() => setError('Payment failed. Please try again.')}
                />
            </div>
        );
    }

    return (
        <>
            {renderMembershipStatus()}

            <div className={styles.choosePlan}>
                {error && (
                    <div className={styles.error}>
                        <p>{error}</p>
                    </div>
                )}

                <h1>{isPaidExtrasContext ? 'Paid Extras' : 'Membership Plans'}</h1>
                <p>{isPaidExtrasContext ? 'Unlock the waitlist, extended booking window, personalized PIN and longer cancellation window.' : 'Choose a plan that works for you.'}</p>

                <div className={styles.plans}>
                    {membershipPlans.map((membershipPlan) => (
                        <div className={styles.plan} key={membershipPlan._id}>

                            <div>
                                <h2>{membershipPlan.name}</h2>
                                <div className={styles.price}>
                                    {selectedDiscountCodes[membershipPlan._id] ? (
                                        <h3>
                                            <strike>£{(membershipPlan.amount / 100).toFixed(2)}</strike> £{(getDiscountedAmount(membershipPlan) / 100).toFixed(2)}
                                        </h3>
                                    ) : (
                                        <h3>£{(membershipPlan.amount / 100).toFixed(2)}</h3>
                                    )}
                                    <p>/ {membershipPlan.interval_count} {membershipPlan.interval}{membershipPlan.interval_count > 1 ? 's' : ''}</p>
                                </div>
                                <p>{membershipPlan.description}</p>

                                <div className={styles.discountCode}>
                                    {selectedDiscountCodes[membershipPlan._id] ? (
                                        <>
                                            <p>Discount Applied: {selectedDiscountCodes[membershipPlan._id].name} ({selectedDiscountCodes[membershipPlan._id].discountType === 'amount' ? `£${((selectedDiscountCodes[membershipPlan._id].amountOff || 0) / 100).toFixed(2)} Off` : `${selectedDiscountCodes[membershipPlan._id].percentageOff}% Off`})</p>
                                            <button type="button" onClick={() => removeDiscountCode(membershipPlan._id)} className="btn btnEmpty">Remove</button>
                                        </>
                                    ) : (
                                        <>
                                            <input
                                                type="text"
                                                placeholder="Discount Code"
                                                value={discountInputs[membershipPlan._id] || ''}
                                                onChange={(event) => setDiscountInputs(prev => ({
                                                    ...prev,
                                                    [membershipPlan._id]: event.target.value
                                                }))}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => applyDiscountCode(membershipPlan._id)}
                                                className="btn btnEmpty"
                                                disabled={discountLoadingPlanId === membershipPlan._id}
                                            >
                                                {discountLoadingPlanId === membershipPlan._id ? 'Applying...' : 'Apply'}
                                            </button>
                                        </>
                                    )}
                                </div>

                                {shouldShowPaymentTypeSelector(membershipPlan) && (
                                    <div className={styles.paymentTypeSelector}>
                                        <p className={styles.paymentTypeLabel}>Choose Payment Option:</p>
                                        <div className={styles.paymentTypeOptions}>
                                            {getAvailablePaymentTypes(membershipPlan).includes('subscription') && (
                                                <label className={`${styles.radioOption} ${getPaymentType(membershipPlan._id) === 'subscription' ? styles.selected : ''}`}>
                                                    <input
                                                        type="radio"
                                                        name={`payment-type-${membershipPlan._id}`}
                                                        value="subscription"
                                                        checked={getPaymentType(membershipPlan._id) === 'subscription'}
                                                        onChange={() => setPaymentType(membershipPlan._id, 'subscription')}
                                                    />
                                                    <div className={styles.optionContent}>
                                                        <span className={styles.optionTitle}>Recurring</span>
                                                        <span className={styles.optionDescription}>Auto-renews every {membershipPlan.interval_count} {membershipPlan.interval}{membershipPlan.interval_count > 1 ? 's' : ''}</span>
                                                    </div>
                                                </label>
                                            )}
                                            {getAvailablePaymentTypes(membershipPlan).includes('one-time') && (
                                                <label className={`${styles.radioOption} ${getPaymentType(membershipPlan._id) === 'one-time' ? styles.selected : ''}`}>
                                                    <input
                                                        type="radio"
                                                        name={`payment-type-${membershipPlan._id}`}
                                                        value="one-time"
                                                        checked={getPaymentType(membershipPlan._id) === 'one-time'}
                                                        onChange={() => setPaymentType(membershipPlan._id, 'one-time')}
                                                    />
                                                    <div className={styles.optionContent}>
                                                        <span className={styles.optionTitle}>One-Time Payment</span>
                                                        <span className={styles.optionDescription}>Single payment for {membershipPlan.interval_count} {membershipPlan.interval}{membershipPlan.interval_count > 1 ? 's' : ''} access</span>
                                                    </div>
                                                </label>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div>
                                <button onClick={() => subscribe(membershipPlan._id, getPaymentType(membershipPlan._id))}
                                        className={"btn btnPrimary"}>Choose
                                    Plan
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {context.domain.choosePlanMessage ? <div className={styles.choosePlanMessage}>
                    <p>{context.domain.choosePlanMessage}</p>
                </div> : null}
            </div>
        </>
    );
}

export default ChoosePlan;
